import { createContext, use, useCallback, useEffect, useId, useRef, useState } from 'react'

/**
 * Waits for the promise `latest` gives, and for any that took its place meanwhile, as a read
 * started again: for a carried contribution's `validate`, which waits for its read to finish.
 */
export async function settled(latest: () => Promise<unknown>): Promise<void> {
  const current = latest()
  await current
  if (current !== latest()) {
    await settled(latest)
  }
}

/** Whether two versions are the same; nginx marks one weak (`W/`) when it compresses a response. */
export function isSameVersion(a: string | null | undefined, b: string | null | undefined): boolean {
  return (a ?? null)?.replace(/^W\//u, '') === (b ?? null)?.replace(/^W\//u, '')
}

/** What a save through an endpoint that shares the host record's version leaves that version at. */
export interface HostVersionAfterSave {
  readonly hostVersion: string | null
  /**
   * The version the write was made at. The form and the other parts take `hostVersion` only when
   * they were at this one, so an edit made at an older version still meets someone else's change.
   * Left out, every one takes it.
   */
  readonly fromVersion?: string | null
}

/**
 * Thrown by a contribution's save whose endpoint shares the host record's version, when one of
 * its writes committed before a later one failed: the form takes `hostVersion`, so its next save
 * does not meet its own write as someone else's, and shows `error`.
 */
export class PartlySavedError extends Error {
  readonly error: unknown
  readonly hostVersion: string | null

  constructor(error: unknown, hostVersion: string | null) {
    super('Part of the save was stored before the rest failed.', { cause: error })
    this.name = 'PartlySavedError'
    this.error = error
    this.hostVersion = hostVersion
  }
}

/**
 * Thrown by a contribution's `validate` that gave up waiting for what it must read before it can
 * save, such as a stalled request: the form says that part did not load, as for one whose code did not.
 */
export class PartNotLoadedError extends Error {
  constructor() {
    super('Part of the form did not finish loading.')
    this.name = 'PartNotLoadedError'
  }
}

/**
 * Why the form did not save a part that saves last (`savesLast`): another part failed, and saving
 * this one could end the access the viewer needs to save that part again.
 */
export class HeldBackError extends Error {
  constructor() {
    super('Held back until the rest of the form saves.')
    this.name = 'HeldBackError'
  }
}

/** What the form tells a contribution's `validate`. */
export interface ValidateContext {
  /** Whether the host's own fields passed; when not, nothing is saved, so a check need not wait for anything. */
  readonly isHostValid: boolean
}

/** What a contributed tab or set of fields gives the form it sits in, to save with it. */
export interface ContributedSave {
  /** Names it in the form's message when it fails to save, such as "Progress". */
  readonly label: string
  /** Whether it holds changes not yet saved. */
  readonly dirty: boolean
  /** Checks its fields, showing their errors; resolves `false` when any is wrong. */
  readonly validate?: (context: ValidateContext) => Promise<boolean>
  /**
   * Whether it is still reading what its check needs, such as the fields it shows. A Save waits
   * for it, as for a part's code; one still loading after that wait did not load, and is never
   * reported as a field to fix.
   */
  readonly isLoading?: boolean
  /**
   * Stores its changes through its own feature's endpoint, for the record `recordId`, then marks
   * what it sent as saved (`markSaved`). Throws (an `ApiError`) when it could not save. One whose
   * endpoint shares the host record's version returns the version it left the host at.
   */
  readonly save: (recordId: string) => Promise<HostVersionAfterSave | void>
  /**
   * Hears the host record's new version each time a Save stores the host, whether or not this
   * contribution has changes: for one whose endpoint shares the host record's version.
   */
  readonly onHostSaved?: (version: string | null, fromVersion?: string | null) => void
  /**
   * Whether it saves to a new record Save As makes even when it holds no change: a section the new
   * record does not get otherwise, such as another area's settings for a copied course, that the
   * viewer may change. Its `validate` waits for it to finish reading, and refuses a failed read, so
   * nothing is left out; its `save` stores nothing it was not allowed to read. One whose save to the
   * new record fails stays owed to it until a save succeeds or `reloaded` is called.
   */
  readonly isCarriedToNewRecord?: boolean
  /**
   * Whether it saves after every other part, as a change that may end the viewer's access to the
   * record would: saved earlier, it would leave the parts after it unable to save. When another
   * part fails it is not saved either, so the viewer can still save that part again.
   */
  readonly savesLast?: boolean
}

/** The form's side of a contribution: what it calls when it saves. */
export interface RegisteredSave {
  readonly label: string
  /** The form tab it sits in. */
  readonly tab: string
  readonly validate: (context: ValidateContext) => Promise<boolean>
  readonly save: (recordId: string) => Promise<HostVersionAfterSave | void>
  /** Passes on the host record's new version after a Save, or a contribution, moved it. */
  readonly hostSaved: (version: string | null, fromVersion?: string | null) => void
  /** Whether its endpoint shares the host record's version (it hears it with `onHostSaved`), so it saves after the others that do. */
  readonly isSharingHostVersion: boolean
  /** Whether it saves to a new record Save As makes even when unchanged (`isCarriedToNewRecord`). */
  readonly isCarriedToNewRecord: () => boolean
  /** Whether it saves after every other part (`savesLast`). */
  readonly savesLast: () => boolean
  /** Whether it is still reading what its check needs (`isLoading`). */
  readonly isLoading: () => boolean
  /** Shows a failed save in the contribution, or clears it with `null`. */
  readonly showError: (error: unknown) => void
}

export interface FormContributions {
  readonly register: (id: string, entry: RegisteredSave) => () => void
  readonly setDirty: (id: string, dirty: boolean) => void
  /** Whether the contribution is still reading what its check needs; a Save waits until none is. */
  readonly setLoading: (id: string, loading: boolean) => void
  /** The contribution was read again, dropping its changes, or is gone: it no longer owes a save to a new record. */
  readonly forgetOwed: (id: string) => void
}

export const FormContributionsContext = createContext<FormContributions | null>(null)

/** The form tab a contribution renders in. */
export const FormTabContext = createContext('')

/**
 * Joins a contributed tab or set of fields to the form it sits in (`RecordForm`), so the form's
 * one Save stores it: after the host's record, through the contributor's own endpoint, so the
 * host never calls another feature. Returns the error of its last save, `null` when it saved.
 * A failed save keeps its edits; show the error with `SaveProblem`.
 */
export function useContributedSave(contribution: ContributedSave): {
  readonly error: unknown
  readonly clearError: () => void
  /** Tells the form the contribution was read again, dropping its changes, as after Reload. */
  readonly reloaded: () => void
} {
  const host = use(FormContributionsContext)
  if (host === null) {
    throw new Error('useContributedSave is for a tab or fields inside a RecordForm.')
  }
  const id = useId()
  const tab = use(FormTabContext)
  const [error, setError] = useState<unknown>(null)
  const latest = useRef(contribution)
  useEffect(() => {
    latest.current = contribution
  })

  const { register, setDirty, setLoading, forgetOwed } = host
  const { label, dirty } = contribution
  const isLoading = contribution.isLoading === true
  useEffect(() => {
    const unregister = register(id, {
      label,
      tab,
      validate: async (context) => (await latest.current.validate?.(context)) ?? true,
      save: async (recordId) => await latest.current.save(recordId),
      hostSaved: (version, fromVersion) => latest.current.onHostSaved?.(version, fromVersion),
      isSharingHostVersion: latest.current.onHostSaved !== undefined,
      isCarriedToNewRecord: () => latest.current.isCarriedToNewRecord === true,
      savesLast: () => latest.current.savesLast === true,
      isLoading: () => latest.current.isLoading === true,
      showError: setError,
    })
    // Registered again, as when the label changes with the language: its changes still count.
    setDirty(id, latest.current.dirty)
    setLoading(id, latest.current.isLoading === true)
    return unregister
  }, [register, setDirty, setLoading, id, label, tab])
  useEffect(() => {
    setDirty(id, dirty)
  }, [setDirty, id, dirty])
  useEffect(() => {
    setLoading(id, isLoading)
  }, [setLoading, id, isLoading])
  // Only once it is gone does it stop owing a save, not each time it registers again.
  useEffect(() => () => forgetOwed(id), [forgetOwed, id])

  // Cleared by the contributor, as when it reloads its record after a conflict.
  const clearError = useCallback(() => setError(null), [])
  const reloaded = useCallback(() => {
    setError(null)
    forgetOwed(id)
  }, [forgetOwed, id])
  return { error, clearError, reloaded }
}
